import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Edit, Trash2, UserCheck, UserX, MapPin, FileText, CalendarCheck, Users, BarChart3, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Doctor, Staff, Room, ScheduleItem, Appointment, Task } from './SchedulerTypes';
import AppointmentsTable from './AppointmentsTable';
import ScheduleLogsTable from './ScheduleLogsTable';
import BackupDAScheduleModal from './BackupDAScheduleModal';
import NurseDoctorProcedureReport from './NurseDoctorProcedureReport';
import SlotManagementReport from './SlotManagementReport';
import { formatDateToString, getWeekDays as getWeekDaysUtil } from '@/lib/dateUtils';
import LoadingOverlay from './LoadingOverlay';

interface TableViewProps {
  currentDate: Date;
  scheduleItems: ScheduleItem[];
  appointments: Appointment[];
  tasks: Task[];
  doctors: Doctor[];
  staff: Staff[];
  rooms: Room[];
  initialTab?: string;
  onAddScheduleItem: (item: Omit<ScheduleItem, 'id'>) => void;
  onUpdateScheduleItem: (id: string, updates: Partial<ScheduleItem>) => void;
  onDeleteScheduleItem: (id: string) => void;
  onDeleteScheduleSeries?: (ids: string[]) => void;
  isLoadingSchedules?: boolean;
  isFetchingSchedules?: boolean;
}

const TableView = ({
  currentDate,
  scheduleItems,
  appointments,
  tasks,
  doctors,
  staff,
  rooms,
  initialTab,
  onAddScheduleItem,
  onUpdateScheduleItem,
  onDeleteScheduleItem,
  isLoadingSchedules = false,
  isFetchingSchedules = false,
}: TableViewProps) => {
  const navigate = useNavigate();
  const [isBackupDAModalOpen, setIsBackupDAModalOpen] = useState(false);
  const [isTabLoading, setIsTabLoading] = useState(false);
  
  // Map URL-friendly tab names to actual tab values
  const tabMapping: { [key: string]: string } = {
    'nurse-doctor-procedure': 'nurse-doctor-procedure',
    'slot-management': 'slot-management',
    'doctor-room-schedule': 'doctor-room',
    'staff-schedule': 'staff',
    'schedule-logs': 'schedule-logs'
  };
  
  // Determine the default tab from the URL or props
  const getDefaultTab = () => {
    if (initialTab && tabMapping[initialTab]) {
      return tabMapping[initialTab];
    }
    return 'nurse-doctor-procedure';
  };
  
  const [activeTab, setActiveTab] = useState(getDefaultTab());
  
  // Update tab when URL changes
  useEffect(() => {
    if (initialTab && tabMapping[initialTab]) {
      setActiveTab(tabMapping[initialTab]);
    }
  }, [initialTab]);
  
  // Handle tab change
  const handleTabChange = (value: string) => {
    setIsTabLoading(true);
    setActiveTab(value);
    // Update URL with the new tab
    const reverseMapping: { [key: string]: string } = {
      'nurse-doctor-procedure': 'nurse-doctor-procedure',
      'slot-management': 'slot-management',
      'doctor-room': 'doctor-room-schedule',
      'staff': 'staff-schedule',
      'schedule-logs': 'schedule-logs'
    };
    navigate(`/report/${reverseMapping[value]}`);

    // Clear loading after minimum display time
    setTimeout(() => setIsTabLoading(false), 300);
  };
  // Get the week dates using Singapore timezone utilities
  // Note: getWeekDaysUtil returns Sunday-Saturday, we need Monday-Saturday
  const weekDays = getWeekDaysUtil(currentDate).slice(1); // Skip Sunday
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const getScheduleForDateAndRoom = (date: Date, roomId: string, timeSlot: 'morning' | 'afternoon') => {
    const dateStr = formatDateToString(date);
    return scheduleItems.filter(item => {
      if (item.date !== dateStr || item.roomId !== roomId) return false;
      
      // Check if the schedule overlaps with the time slot
      const startTime = item.startTime;
      const endTime = item.endTime;
      
      if (timeSlot === 'morning') {
        // Morning: 00:00 - 11:59
        // Show if schedule starts before 12:00 OR ends after 00:00 (which it always does)
        return startTime < '12:00';
      } else {
        // Afternoon: 12:00 - 23:59  
        // Show if schedule starts before 24:00 (which it always does) AND ends after 12:00
        return endTime > '12:00';
      }
    });
  };

  const getStaffForDate = (date: Date, role: string) => {
    const dateStr = formatDateToString(date);
    return scheduleItems.filter(item => 
      item.date === dateStr && 
      item.staffId && 
      staff.find(s => s.id === item.staffId)?.role.includes(role)
    );
  };

  const getDoctorById = (id: string) => doctors.find(d => d.id === id);
  const getStaffById = (id: string) => staff.find(s => s.id === id);
  const getRoomById = (id: string) => rooms.find(r => r.id === id);

  const renderScheduleCell = (date: Date, roomId: string, timeSlot: 'morning' | 'afternoon') => {
    const schedules = getScheduleForDateAndRoom(date, roomId, timeSlot);
    
    // Group schedules by unique doctor/staff combination and type
    const groupedSchedules = schedules.reduce((acc, item) => {
      const key = `${item.doctorId || ''}-${item.staffId || ''}-${item.type}`;
      if (!acc[key]) {
        acc[key] = item;
      }
      return acc;
    }, {} as Record<string, typeof schedules[0]>);
    
    const uniqueSchedules = Object.values(groupedSchedules);
    
    return (
      <TableCell key={`${formatDateToString(date)}-${roomId}-${timeSlot}`} className="p-1 border border-border min-w-[80px] sm:min-w-[120px]">
        <div className="space-y-1">
          {uniqueSchedules.length === 0 ? (
            <div className="text-xs text-center py-2 bg-green-100 text-green-800 rounded font-medium">Available</div>
          ) : (
            uniqueSchedules.map(item => {
              const doctor = item.doctorId ? getDoctorById(item.doctorId) : null;
              const staffMember = item.staffId ? getStaffById(item.staffId) : null;
              
              return (
                <div
                  key={`${item.doctorId || ''}-${item.staffId || ''}-${item.type}`}
                  className={`text-xs p-1 rounded text-center font-medium whitespace-pre-line ${
                    item.type === 'mc' 
                      ? 'bg-red-700 text-white'
                      : item.type === 'leave'
                      ? 'bg-red-500 text-white' 
                      : doctor 
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-purple-100 text-purple-800'
                  }`}
                >
                  {item.type === 'leave' ? 'AL' : 
                   item.type === 'mc' ? 'UL' :
                   doctor && staffMember ? 
                     `${doctor.short_name || doctor.name}\n${staffMember.short_name || staffMember.name}` :
                   doctor ? (doctor.short_name || doctor.name) :
                   staffMember ? (staffMember.short_name || staffMember.name) : 
                   item.title}
                </div>
              );
            })
          )}
        </div>
      </TableCell>
    );
  };

  const renderStaffCell = (date: Date, staffNames: string[]) => {
    const staffSchedules = staffNames.map(name => {
      const dateStr = formatDateToString(date);
      const staffMember = staff.find(s => s.name.includes(name));
      if (!staffMember) return null;
      
      const schedules = scheduleItems.filter(item => 
        item.date === dateStr && item.staffId === staffMember.id
      );
      
      return { staff: staffMember, schedules };
    }).filter(Boolean);

    return (
      <TableCell key={formatDateToString(date)} className="p-1 border border-border min-w-[60px] sm:min-w-[80px]">
        <div className="space-y-1">
          {staffSchedules.length === 0 ? (
            <div className="text-xs text-center py-1 text-muted-foreground">-</div>
          ) : (
            staffSchedules.map(({ staff, schedules }) => {
              if (schedules.length === 0) return (
                <div key={staff?.id} className="text-xs text-center py-1 text-muted-foreground">-</div>
              );
              
              return schedules.map(schedule => (
                <div
                  key={schedule.id}
                  className={`text-xs p-1 rounded text-center font-medium ${
                    schedule.type === 'mc' 
                      ? 'bg-red-700 text-white'
                      : schedule.type === 'leave'
                      ? 'bg-red-500 text-white' 
                      : 'bg-green-100 text-green-800'
                  }`}
                >
                  {schedule.type === 'leave' ? 'AL' : 
                   schedule.type === 'mc' ? 'UL' :
                   (staff?.short_name || staff?.name)}
                </div>
              ));
            })
          )}
        </div>
      </TableCell>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg sm:text-xl font-semibold">Weekly Schedule Overview</h2>
        <div className="flex items-center space-x-1 sm:space-x-2 overflow-x-auto">
          <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs shrink-0">Doctor</Badge>
          <Badge variant="outline" className="bg-purple-50 text-purple-700 text-xs shrink-0">Staff</Badge>
          <Badge variant="outline" className="bg-red-50 text-red-700 text-xs shrink-0">Leave/MC</Badge>
          <Badge variant="outline" className="bg-green-50 text-green-700 text-xs shrink-0">Available</Badge>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <div className="overflow-x-auto -mx-2 px-2 pb-2">
          <TabsList className="grid w-fit min-w-full sm:w-full grid-cols-5">
            <TabsTrigger value="nurse-doctor-procedure" className="flex items-center gap-1 text-xs sm:text-sm px-1.5 sm:px-3">
              <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
              <span className="hidden sm:inline">Nurse Doctor Procedure</span>
              <span className="sm:hidden truncate">NDP</span>
            </TabsTrigger>
            <TabsTrigger value="slot-management" className="flex items-center gap-1 text-xs sm:text-sm px-1.5 sm:px-3">
              <BarChart3 className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
              <span className="hidden sm:inline">Slot Management</span>
              <span className="sm:hidden truncate">Slots</span>
            </TabsTrigger>
            <TabsTrigger value="doctor-room" className="flex items-center gap-1 text-xs sm:text-sm px-1.5 sm:px-3">
              <MapPin className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
              <span className="hidden sm:inline">Doctor & Room</span>
              <span className="sm:hidden truncate">Dr/Room</span>
            </TabsTrigger>
            <TabsTrigger value="staff" className="flex items-center gap-1 text-xs sm:text-sm px-1.5 sm:px-3">
              <UserCheck className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
              <span className="hidden sm:inline">Staff Schedule</span>
              <span className="sm:hidden truncate">Staff</span>
            </TabsTrigger>
            <TabsTrigger value="schedule-logs" className="flex items-center gap-1 text-xs sm:text-sm px-1.5 sm:px-3">
              <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
              <span className="hidden sm:inline">Schedule Logs</span>
              <span className="sm:hidden truncate">Logs</span>
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="nurse-doctor-procedure">
          <NurseDoctorProcedureReport
            scheduleItems={scheduleItems}
            doctors={doctors}
            staff={staff}
            rooms={rooms}
            currentDate={currentDate}
          />
        </TabsContent>

        <TabsContent value="slot-management">
          <SlotManagementReport
            scheduleItems={scheduleItems}
            rooms={rooms}
            doctors={doctors}
            staff={staff}
            currentDate={currentDate}
          />
        </TabsContent>

        <TabsContent value="doctor-room">
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold flex items-center space-x-2">
                <MapPin className="h-5 w-5" />
                <span>Doctor & Room Schedule</span>
              </h3>
            </CardHeader>
            <CardContent className="px-2 sm:px-6">
              <div className="overflow-x-auto -mx-2 sm:mx-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-24 sm:w-32">Room/Time</TableHead>
                      {weekDays.map((day, index) => (
                        <TableHead key={formatDateToString(day)} className="text-center min-w-[80px] sm:min-w-[120px]">
                          <div className="space-y-1">
                            <div className="font-semibold text-xs sm:text-sm">{dayNames[index].slice(0, 3)}<span className="hidden sm:inline">{dayNames[index].slice(3)}</span></div>
                            <div className="text-xs sm:text-sm text-muted-foreground">{day.getDate()} {day.toLocaleDateString('en-SG', { month: 'short' })}</div>
                          </div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rooms.filter(room => room.status === 'active').map(room => (
                      <React.Fragment key={room.id}>
                        <TableRow>
                          <TableCell className="font-medium bg-muted/30">
                            <div className="space-y-1">
                              <div className="text-sm font-semibold">{room.name}</div>
                              <div className="text-xs text-muted-foreground">{room.clinic}</div>
                              <Badge variant="outline">AM</Badge>
                            </div>
                          </TableCell>
                          {weekDays.map(day => renderScheduleCell(day, room.id, 'morning'))}
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium bg-muted/30">
                            <div className="space-y-1">
                              <div className="text-sm font-semibold">{room.name}</div>
                              <div className="text-xs text-muted-foreground">{room.clinic}</div>
                              <Badge variant="outline">PM</Badge>
                            </div>
                          </TableCell>
                          {weekDays.map(day => renderScheduleCell(day, room.id, 'afternoon'))}
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="staff">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="text-base sm:text-lg font-semibold flex items-center space-x-2">
                  <UserCheck className="h-5 w-5" />
                  <span>Staff Schedule</span>
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsBackupDAModalOpen(true)}
                  className="flex items-center space-x-2"
                >
                  <Users className="h-4 w-4" />
                  <span className="hidden sm:inline">Backup DA Schedule</span>
                  <span className="sm:hidden">Backup DA</span>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="px-2 sm:px-6">
              <div className="overflow-x-auto -mx-2 sm:mx-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-24 sm:w-32">Staff Role</TableHead>
                      {weekDays.map((day, index) => (
                        <TableHead key={formatDateToString(day)} className="text-center min-w-[80px] sm:min-w-[100px]">
                          <div className="space-y-1">
                            <div className="font-semibold">{dayNames[index]}</div>
                            <div className="text-sm text-muted-foreground">{day.getDate()} {day.toLocaleDateString('en-SG', { month: 'short' })}</div>
                          </div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium bg-muted/30">
                        <div className="space-y-1">
                          <div className="text-sm font-semibold">Nurses</div>
                          <div className="text-xs text-muted-foreground">N1 & N2</div>
                        </div>
                      </TableCell>
                      {weekDays.map(day => renderStaffCell(day, ['PUI', 'LIZ', 'MIU', 'LIYUAN']))}
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium bg-muted/30">
                        <div className="space-y-1">
                          <div className="text-sm font-semibold">Nurses GP</div>
                          <div className="text-xs text-muted-foreground">General Practice</div>
                        </div>
                      </TableCell>
                      {weekDays.map(day => renderStaffCell(day, ['TIFFANY', 'PHOEBE']))}
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium bg-muted/30">
                        <div className="space-y-1">
                          <div className="text-sm font-semibold">Reception</div>
                          <div className="text-xs text-muted-foreground">Front Desk</div>
                        </div>
                      </TableCell>
                      {weekDays.map(day => renderStaffCell(day, ['MANFRED', 'CARMEN', 'CHAVI']))}
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium bg-muted/30">
                        <div className="space-y-1">
                          <div className="text-sm font-semibold">Dental Assistant</div>
                          <div className="text-xs text-muted-foreground">Chair-side</div>
                        </div>
                      </TableCell>
                      {weekDays.map(day => renderStaffCell(day, ['AIM', 'MEWKY']))}
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>



        <TabsContent value="schedule-logs">
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold flex items-center space-x-2">
                <FileText className="h-5 w-5" />
                <span>Schedule Logs</span>
              </h3>
            </CardHeader>
            <CardContent>
              <ScheduleLogsTable
                scheduleItems={scheduleItems}
                doctors={doctors}
                staff={staff}
                rooms={rooms}
                onEditScheduleItem={(item) => {
                  // Open the schedule modal with the item data for editing
                  // This would typically be handled by a state in the parent component
                  // For now, we'll just call the update function directly
                  console.log('Edit schedule item:', item);
                  // You would typically show a modal here with the item data
                  // and then call onUpdateScheduleItem when the user saves
                  // For demonstration, we'll just update a dummy field
                  onUpdateScheduleItem(item.id, item);
                }}
                onDeleteScheduleItem={(id) => {
                  onDeleteScheduleItem(id);
                }}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Backup DA Schedule Modal */}
      <BackupDAScheduleModal
        isOpen={isBackupDAModalOpen}
        onClose={() => setIsBackupDAModalOpen(false)}
        currentDate={currentDate}
        doctors={doctors}
        staff={staff}
        rooms={rooms}
        scheduleItems={scheduleItems}
      />

      <LoadingOverlay
        isLoading={isLoadingSchedules || isTabLoading}
        message={isTabLoading ? "Switching report..." : "Loading report data..."}
      />
    </div>
  );
};

export default TableView;
